import data from './branches.json';
import { listingBranchIds, visibleBranchIds } from './branchScope';

/**
 * Interruptor de sucursales.
 *
 * - Web publica (VITE_MULTI_BRANCH sin definir o `false`): solo existen las
 *   sucursales con `prelaunch: false`, y del stock de EBM solo se muestra el de
 *   sus `ebmBranchIds`. Asi, si Santiago ya tiene inventario cargado en EBM
 *   antes de abrir, el publico no lo ve.
 * - Version de prueba (`VITE_MULTI_BRANCH=true`): aparecen todas, y el stock
 *   de cualquier branchId de EBM, para poder probar con datos reales.
 *
 * Dia de la apertura: `prelaunch: false` en branches.json (y, si ya se quiere
 * el selector y demas, VITE_MULTI_BRANCH=true en .env.production).
 */
export const MULTI_BRANCH = import.meta.env.VITE_MULTI_BRANCH === 'true';

export interface Branch {
  key: string;
  name: string;
  city: string;
  prelaunch: boolean;
  ebmBranchIds: number[];
  address: string;
  mapsUrl: string;
  mapsEmbed: string;
  phones: { label: string; tel: string }[];
  whatsapp: string;
  email: string;
  hours: { days: string; hours: string }[];
}

export const ALL_BRANCHES: Branch[] = data.branches;

/** Sucursales que se muestran en esta version del sitio. */
export const BRANCHES: Branch[] = ALL_BRANCHES.filter((b) => MULTI_BRANCH || !b.prelaunch);

const publicEbmIds = new Set(BRANCHES.flatMap((b) => b.ebmBranchIds));

/** Sucursal principal: la que usan los datos sin sucursal (carritos viejos, etc.). */
export const MAIN_BRANCH: Branch = ALL_BRANCHES[0];

/** Hay que dejar elegir sucursal: interruptor encendido y mas de una visible. */
export const BRANCH_PICKER = MULTI_BRANCH && BRANCHES.length > 1;

/** Donde se guarda la sucursal elegida por el visitante (la lee también BranchContext). */
export const BRANCH_STORAGE_KEY = 'spartech_branch';

function storedBranchKey(): string | null {
  if (!BRANCH_PICKER) return null;
  try {
    return localStorage.getItem(BRANCH_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Sucursales de EBM del listado (catálogo, categorías, marcas, Arma tu PC): la elegida
 * con el selector, o todas las visibles en la web pública. Se manda como `?branchIds=`.
 */
export function catalogBranchIds(): number[] {
  return listingBranchIds(BRANCHES, BRANCH_PICKER, storedBranchKey());
}

/** Sucursales de EBM de la ficha: todas las visibles (disponibilidad en otra sucursal). */
export function detailBranchIds(): number[] {
  return visibleBranchIds(BRANCHES);
}

export function branchByKey(key: string | null | undefined): Branch {
  return BRANCHES.find((b) => b.key === key) ?? MAIN_BRANCH;
}

/** Stock y menor precio de un producto en una sucursal (segun sus ebmBranchIds). */
export function stockIn(
  availability: { branchId?: number | null; quantity: number; price: number }[],
  branch: Branch,
): { quantity: number; price: number } {
  const rows = availability.filter((a) => a.branchId != null && branch.ebmBranchIds.includes(a.branchId));
  const prices = rows.map((a) => a.price).filter((p) => p > 0);
  return {
    quantity: rows.reduce((sum, a) => sum + a.quantity, 0),
    price: prices.length ? Math.min(...prices) : 0,
  };
}

/** ¿Se puede mostrar el stock de este branchId de EBM? */
export function isEbmBranchVisible(branchId: number | null | undefined): boolean {
  // Sin branchId (respuestas viejas o mocks) no hay forma de separar: se muestra.
  if (MULTI_BRANCH || branchId == null) return true;
  return publicEbmIds.has(branchId);
}
